import { useEffect, useState, useRef, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usersApi } from './api';
import { tenantsApi, type Tenant } from '../api/tenants-api';
import { taxStatusApi, type TaxStatusCategory } from '../api/tax-status-api';
import type { CreateUserDto, UpdateUserDto } from './types';
import { MapViewer } from '../map-engine';
import { MapControls } from '../map-engine/MapControls';
import { MapStyleSelector } from '../map-engine/MapStyleSelector';
import { ErrorToast } from '../projects/components/ErrorToast';
import { getErrorMessage } from '../utils/error-helpers';
import type { Map as MapLibreMap } from 'maplibre-gl';
import './user-form.css';

/**
 * Países de América (Norte, Centro y Sur)
 */
const AMERICAN_COUNTRIES = [
  'Argentina', 'Bolivia', 'Brasil', 'Chile', 'Colombia', 'Ecuador', 'Guyana',
  'Paraguay', 'Perú', 'Surinam', 'Uruguay', 'Venezuela', // Sudamérica
  'Costa Rica', 'El Salvador', 'Guatemala', 'Honduras', 'Nicaragua', 'Panamá', // Centroamérica
  'Belice', 'México', // Norte de Centroamérica
  'Canadá', 'Estados Unidos', // Norteamérica
  'Antigua y Barbuda', 'Bahamas', 'Barbados', 'Cuba', 'Dominica', 'Granada',
  'Haití', 'Jamaica', 'República Dominicana', 'San Cristóbal y Nieves',
  'Santa Lucía', 'San Vicente y las Granadinas', 'Trinidad y Tobago', // Caribe
];

/**
 * Idiomas disponibles para contacto
 */
const AVAILABLE_LANGUAGES = [
  { code: 'es', name: 'Español' },
  { code: 'pt', name: 'Portugués' },
  { code: 'en', name: 'Inglés' },
  { code: 'fr', name: 'Francés' },
];

/**
 * Mapeo de nombres de países en español a inglés (para compatibilidad con datos de mapa)
 */
const COUNTRY_NAME_MAPPING: Record<string, string> = {
  'Argentina': 'Argentina',
  'Bolivia': 'Bolivia',
  'Brasil': 'Brazil',
  'Chile': 'Chile',
  'Colombia': 'Colombia',
  'Ecuador': 'Ecuador',
  'Guyana': 'Guyana',
  'Paraguay': 'Paraguay',
  'Perú': 'Peru',
  'Surinam': 'Suriname',
  'Uruguay': 'Uruguay',
  'Venezuela': 'Venezuela',
  'Costa Rica': 'Costa Rica',
  'El Salvador': 'El Salvador',
  'Guatemala': 'Guatemala',
  'Honduras': 'Honduras',
  'Nicaragua': 'Nicaragua',
  'Panamá': 'Panama',
  'Belice': 'Belize',
  'México': 'Mexico',
  'Canadá': 'Canada',
  'Estados Unidos': 'United States',
  'Antigua y Barbuda': 'Antigua and Barbuda',
  'Bahamas': 'Bahamas',
  'Barbados': 'Barbados',
  'Cuba': 'Cuba',
  'Dominica': 'Dominica',
  'Granada': 'Grenada',
  'Haití': 'Haiti',
  'Jamaica': 'Jamaica',
  'República Dominicana': 'Dominican Republic',
  'San Cristóbal y Nieves': 'Saint Kitts and Nevis',
  'Santa Lucía': 'Saint Lucia',
  'San Vicente y las Granadinas': 'Saint Vincent and the Grenadines',
  'Trinidad y Tobago': 'Trinidad and Tobago',
};

/**
 * UserForm - Formulario para crear/editar usuarios (SuperAdmin)
 *
 * @remarks
 * Layout fullscreen con mapa de fondo y sidebar glassmorphic.
 * Incluye mapa interactivo que muestra los países del continente americano con:
 * - Hover effect: países se iluminan suavemente al pasar el mouse
 * - Controles personalizados: MapControls (zoom, brújula, 2D/3D, estilos) con glassmorphism
 * - Selector de estilo: MapStyleSelector modal para cambiar entre 5 estilos de mapa
 * Mismo diseño que ClientForm para consistencia visual.
 *
 * **Cumple con:**
 * - P1: Componente < 700 líneas
 * - P3: Documentación JSDoc completa
 */
export function UserForm(): JSX.Element {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  /**
   * Estado del formulario de usuario
   */
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    alias: '',
    country: '',
    phone: '',
    address: '',
    languages: [] as string[],
    taxStatus: '',
    taxIdNumber: '',
    password: '',
    roleName: 'user_low' as 'admin' | 'user_high' | 'user_medium' | 'user_low',
    tenantId: '',
    isActive: true,
  });

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [taxStatusCategories, setTaxStatusCategories] = useState<TaxStatusCategory[]>([]);
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Referencias para el mapa
  const mapRef = useRef<MapLibreMap | null>(null);

  // Estado para tracking de país en hover
  const [hoveredCountry, setHoveredCountry] = useState<string | null>(null);

  // Estados para controles de mapa
  const [showStyleSelector, setShowStyleSelector] = useState(false);
  const [_viewMode, setViewMode] = useState<'2D' | '3D'>('2D');

  /**
   * Carga datos del usuario si estamos en modo edición
   */
  useEffect(() => {
    if (isEditMode && id) {
      const loadUser = async () => {
        try {
          setIsLoading(true);
          const user = await usersApi.get(id);

          setFormData({
            email: user.email,
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            alias: user.alias || '',
            country: (user as any).country || '',
            phone: (user as any).phone || '',
            address: (user as any).address || '',
            languages: (user as any).languages || [],
            taxStatus: user.taxStatus || '',
            taxIdNumber: user.taxIdNumber || '',
            password: '',
            roleName: user.role.name as any,
            tenantId: user.tenant?.id || '',
            isActive: user.isActive,
          });
        } catch (err) {
          console.error('Error cargando usuario:', err);
          setError(getErrorMessage(err, 'Error al cargar los datos del usuario'));
        } finally {
          setIsLoading(false);
        }
      };

      void loadUser();
    }
  }, [isEditMode, id]);

  /**
   * Carga tenants al montar
   */
  useEffect(() => {
    const loadTenants = async () => {
      try {
        const data = await tenantsApi.getAll();
        setTenants(data);
      } catch (err) {
        console.error('Error cargando tenants:', err);
      }
    };

    void loadTenants();
  }, []);

  /**
   * Carga categorías de tax status al montar
   */
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await taxStatusApi.list();
        setTaxStatusCategories(data);
      } catch (err) {
        console.error('Error cargando categorías de tax status:', err);
      }
    };

    void loadCategories();
  }, []);

  /**
   * Maneja la carga del mapa
   */
  const handleMapLoad = (map: MapLibreMap) => {
    mapRef.current = map;

    // Agregar fuente de datos de países (Natural Earth Data)
    map.addSource('countries', {
      type: 'geojson',
      data: 'https://raw.githubusercontent.com/nvkelso/natural-earth-vector/master/geojson/ne_110m_admin_0_countries.geojson',
    });

    // Capa base invisible para capturar eventos (debe estar primero)
    map.addLayer({
      id: 'countries-base',
      type: 'fill',
      source: 'countries',
      paint: {
        'fill-color': 'transparent',
        'fill-opacity': 0,
      },
    });

    // Capa de relleno para hover (debajo de todo)
    map.addLayer({
      id: 'countries-fill-hover',
      type: 'fill',
      source: 'countries',
      paint: {
        'fill-color': '#2FA4B5',
        'fill-opacity': 0.2,
      },
      filter: ['==', 'NAME', ''], // Inicialmente ninguno
    });

    // Capa de relleno para país seleccionado (encima del hover)
    map.addLayer({
      id: 'countries-fill-selected',
      type: 'fill',
      source: 'countries',
      paint: {
        'fill-color': '#2FA4B5',
        'fill-opacity': 0.7, // Aumentado de 0.5 a 0.7 para mejor visibilidad
      },
      filter: ['==', 'NAME', ''], // Inicialmente ninguno
    });

    // Capa de borde para todos los países
    map.addLayer({
      id: 'countries-border',
      type: 'line',
      source: 'countries',
      paint: {
        'line-color': 'rgba(47, 164, 181, 0.3)',
        'line-width': 0.5,
      },
    });

    // Capa de borde exterior para país seleccionado (glow effect)
    map.addLayer({
      id: 'countries-border-selected-glow',
      type: 'line',
      source: 'countries',
      paint: {
        'line-color': '#2FA4B5',
        'line-width': 6,
        'line-blur': 4, // Efecto de difuminado para simular glow
        'line-opacity': 0.4,
      },
      filter: ['==', 'NAME', ''],
    });

    // Capa de borde para país seleccionado (línea sólida)
    map.addLayer({
      id: 'countries-border-selected',
      type: 'line',
      source: 'countries',
      paint: {
        'line-color': '#2FA4B5',
        'line-width': 3, // Aumentado de 2 a 3 para mejor visibilidad
        'line-opacity': 1,
      },
      filter: ['==', 'NAME', ''],
    });

    // Hover effect - escuchar eventos en la capa base
    map.on('mousemove', 'countries-base', (e) => {
      if (e.features && e.features.length > 0) {
        const feature = e.features[0];
        const countryName = feature.properties?.NAME || feature.properties?.name;

        if (countryName) {
          setHoveredCountry(countryName);
          map.getCanvas().style.cursor = 'pointer';
        }
      }
    });

    map.on('mouseleave', 'countries-base', () => {
      setHoveredCountry(null);
      map.getCanvas().style.cursor = '';
    });
  };

  /**
   * Actualiza el hover effect en el mapa
   */
  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;

    // Solo actualizar si el layer existe
    if (map.getLayer('countries-fill-hover')) {
      map.setFilter('countries-fill-hover',
        hoveredCountry ? ['==', 'NAME', hoveredCountry] : ['==', 'NAME', '']
      );
    }
  }, [hoveredCountry]);

  /**
   * Sincroniza el país seleccionado con el mapa
   */
  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current;
    const selectedCountryEnglish = COUNTRY_NAME_MAPPING[formData.country];

    // Verificar que las capas existen antes de actualizar
    if (!map.getLayer('countries-fill-selected') ||
        !map.getLayer('countries-border-selected') ||
        !map.getLayer('countries-border-selected-glow')) {
      return;
    }

    // Actualizar filtros para mostrar el país seleccionado
    if (selectedCountryEnglish) {
      map.setFilter('countries-fill-selected', ['==', 'NAME', selectedCountryEnglish]);
      map.setFilter('countries-border-selected', ['==', 'NAME', selectedCountryEnglish]);
      map.setFilter('countries-border-selected-glow', ['==', 'NAME', selectedCountryEnglish]);
    } else {
      // Limpiar selección
      map.setFilter('countries-fill-selected', ['==', 'NAME', '']);
      map.setFilter('countries-border-selected', ['==', 'NAME', '']);
      map.setFilter('countries-border-selected-glow', ['==', 'NAME', '']);
    }
  }, [formData.country]);

  /**
   * Crea nueva categoría de tax status
   */
  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;

    try {
      const newCategory = await taxStatusApi.create({ name: newCategoryName.trim() });
      setTaxStatusCategories([...taxStatusCategories, newCategory]);
      setFormData({ ...formData, taxStatus: newCategory.name });
      setShowNewCategoryInput(false);
      setNewCategoryName('');
    } catch (err: any) {
      setError(getErrorMessage(err, 'Error creando categoría'));
    }
  };

  /**
   * Maneja el toggle de idiomas
   */
  const handleLanguageToggle = (langCode: string) => {
    setFormData(prev => {
      const isSelected = prev.languages.includes(langCode);
      return {
        ...prev,
        languages: isSelected
          ? prev.languages.filter(l => l !== langCode)
          : [...prev.languages, langCode],
      };
    });
  };

  /**
   * Maneja el submit del formulario
   */
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validaciones
    if (!formData.email.trim()) {
      setError('El correo electrónico es requerido');
      return;
    }

    if (!formData.firstName.trim()) {
      setError('El nombre es requerido');
      return;
    }

    if (!formData.lastName.trim()) {
      setError('El apellido es requerido');
      return;
    }

    if (!isEditMode && !formData.password.trim()) {
      setError('La contraseña es requerida');
      return;
    }

    if (formData.password.trim() && formData.password.length < 8) {
      setError('La contraseña debe tener mínimo 8 caracteres');
      return;
    }

    if (!formData.tenantId) {
      setError('Debe seleccionar un cliente');
      return;
    }

    try {
      setIsSaving(true);

      if (isEditMode && id) {
        // Update user
        const updateData: UpdateUserDto = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          alias: formData.alias.trim() || undefined,
          country: formData.country.trim() || undefined,
          phone: formData.phone.trim() || undefined,
          address: formData.address.trim() || undefined,
          languages: formData.languages.length > 0 ? formData.languages : undefined,
          taxStatus: formData.taxStatus.trim() || undefined,
          taxIdNumber: formData.taxIdNumber.trim() || undefined,
          roleName: formData.roleName,
          isActive: formData.isActive,
          tenantId: formData.tenantId || undefined,
        };

        if (formData.password.trim() !== '') {
          updateData.password = formData.password;
        }

        await usersApi.update(id, updateData);
        console.log('✅ Usuario actualizado exitosamente');
        navigate('/admin/dashboard?tab=users');
      } else {
        // Create user
        const createData: CreateUserDto = {
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          alias: formData.alias.trim() || undefined,
          country: formData.country.trim() || undefined,
          phone: formData.phone.trim() || undefined,
          address: formData.address.trim() || undefined,
          languages: formData.languages.length > 0 ? formData.languages : undefined,
          taxStatus: formData.taxStatus.trim() || undefined,
          taxIdNumber: formData.taxIdNumber.trim() || undefined,
          roleName: formData.roleName,
          tenantId: formData.tenantId,
        };

        await usersApi.create(createData);
        console.log('✅ Usuario creado exitosamente');
        navigate('/admin/dashboard?tab=users');
      }
    } catch (err: any) {
      console.error('❌ Error al guardar usuario:', err);
      setError(getErrorMessage(err, 'Error al guardar el usuario'));
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="user-form-container">
        <div className="user-form-map-background" />
        <div className="user-form">
          <div className="loading-spinner">Cargando...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-form-container">
      {/* Toast de error emergente (centro superior) */}
      <ErrorToast
        message={error}
        onClose={() => setError(null)}
      />

      {/* Mapa de fondo interactivo */}
      <div className="user-form-map-background">
        <MapViewer
          onMapLoad={handleMapLoad}
          showControls={false}
          showScale={true}
        />

        {/* Controles personalizados de mapa (glassmorphism WESTEER) */}
        <MapControls
          map={mapRef.current}
          onViewModeChange={setViewMode}
          onStyleChange={() => setShowStyleSelector(true)}
        />

        {/* Selector de estilo de mapa */}
        <MapStyleSelector
          map={mapRef.current}
          isOpen={showStyleSelector}
          onClose={() => setShowStyleSelector(false)}
        />
      </div>

      {/* Sidebar glassmorphic */}
      <form onSubmit={handleSubmit} className="user-form">
        {/* Header con logo y título */}
        <div className="user-form-header">
          <div className="user-form-logo-container">
            <img
              src="/images/westeer-logo-white.png"
              alt="WESTEER"
              className="user-form-logo"
            />
          </div>
          <div className="user-form-title-section">
            <h1 className="user-form-title">
              {isEditMode ? 'Editar Usuario' : 'Crear Nuevo Usuario'}
            </h1>
            <p className="user-form-subtitle">
              {isEditMode
                ? 'Modifica la información del usuario'
                : 'Completa los datos del nuevo usuario'}
            </p>
          </div>
        </div>

        {/* Contenido scrolleable */}
        <div className="user-form-content">
          <div className="form-section">
            <h2 className="section-title">Información Personal</h2>

            <div className="form-group">
              <label htmlFor="firstName">
                Nombre <span className="required">*</span>
              </label>
              <input
                id="firstName"
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                required
                disabled={isSaving}
                placeholder="Juan"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="lastName">
                Apellido <span className="required">*</span>
              </label>
              <input
                id="lastName"
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                required
                disabled={isSaving}
                placeholder="Pérez"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="alias">Alias</label>
              <input
                id="alias"
                type="text"
                value={formData.alias}
                onChange={(e) => setFormData({ ...formData, alias: e.target.value })}
                disabled={isSaving}
                placeholder="Juancho (opcional)"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">
                Correo Electrónico <span className="required">*</span>
              </label>
              <input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                disabled={isSaving || isEditMode}
                placeholder="usuario@ejemplo.com"
                className="form-input"
              />
              {isEditMode && (
                <small className="form-help">
                  El correo electrónico no se puede modificar
                </small>
              )}
            </div>
          </div>

          <div className="form-section">
            <h2 className="section-title">Datos de Contacto</h2>

            <div className="form-group">
              <label htmlFor="country">País</label>
              <select
                id="country"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                disabled={isSaving}
                className="form-select"
              >
                <option value="">Seleccionar país</option>
                {AMERICAN_COUNTRIES.map(country => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </select>
              <small className="form-help">
                País de residencia del usuario
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="phone">Teléfono</label>
              <input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                disabled={isSaving}
                placeholder="+54 291 123-4567"
                className="form-input"
              />
              <small className="form-help">
                Número de teléfono con código de área
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="address">Dirección</label>
              <textarea
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                disabled={isSaving}
                placeholder="Dirección completa (opcional)"
                className="form-textarea"
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>Idiomas de Comunicación</label>
              <div className="languages-checklist">
                {AVAILABLE_LANGUAGES.map((lang) => (
                  <div key={lang.code} className="language-checkbox-item">
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.languages.includes(lang.code)}
                        disabled={isSaving}
                        onChange={() => handleLanguageToggle(lang.code)}
                      />
                      <span className="language-name">{lang.name}</span>
                    </label>
                  </div>
                ))}
              </div>
              <small className="form-help">
                Idiomas en los que el usuario puede comunicarse
              </small>
            </div>
          </div>

          <div className="form-section">
            <h2 className="section-title">Información Fiscal</h2>

            <div className="form-group">
              <label htmlFor="taxStatus">Condición IVA / Tax Status</label>
              {!showNewCategoryInput ? (
                <>
                  <select
                    id="taxStatus"
                    value={formData.taxStatus}
                    onChange={(e) => {
                      if (e.target.value === '__new__') {
                        setShowNewCategoryInput(true);
                        setFormData({ ...formData, taxStatus: '' });
                      } else {
                        setFormData({ ...formData, taxStatus: e.target.value });
                      }
                    }}
                    disabled={isSaving}
                    className="form-select"
                  >
                    <option value="">Seleccionar (opcional)</option>

                    {/* Agrupar por país */}
                    {['Argentina', 'Canadá', 'Estados Unidos', 'México'].map((country) => {
                      const countryCategories = taxStatusCategories.filter(
                        (cat) => cat.countryName === country
                      );
                      if (countryCategories.length === 0) return null;

                      return (
                        <optgroup key={country} label={country}>
                          {countryCategories.map((cat) => (
                            <option key={cat.id} value={cat.name}>
                              {cat.name}
                            </option>
                          ))}
                        </optgroup>
                      );
                    })}

                    {/* Categorías sin país */}
                    {taxStatusCategories.filter((cat) => !cat.countryName).length > 0 && (
                      <optgroup label="Otras">
                        {taxStatusCategories
                          .filter((cat) => !cat.countryName)
                          .map((cat) => (
                            <option key={cat.id} value={cat.name}>
                              {cat.name}
                            </option>
                          ))}
                      </optgroup>
                    )}

                    <option value="__new__">+ Agregar nueva categoría</option>
                  </select>
                  <small className="form-help">
                    Selecciona una categoría existente o crea una nueva
                  </small>
                </>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <input
                      type="text"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      placeholder="Nombre de la nueva categoría"
                      className="form-input"
                      style={{ flex: 1 }}
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={handleCreateCategory}
                      disabled={!newCategoryName.trim()}
                      className="btn-primary"
                      style={{ padding: '7px 12px', whiteSpace: 'nowrap' }}
                    >
                      Crear
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowNewCategoryInput(false);
                        setNewCategoryName('');
                      }}
                      className="btn-secondary"
                      style={{ padding: '7px 12px', whiteSpace: 'nowrap' }}
                    >
                      Cancelar
                    </button>
                  </div>
                  <small className="form-help">
                    Esta categoría quedará disponible para todos los usuarios
                  </small>
                </>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="taxIdNumber">ID Fiscal / CUIT</label>
              <input
                id="taxIdNumber"
                type="text"
                value={formData.taxIdNumber}
                onChange={(e) => setFormData({ ...formData, taxIdNumber: e.target.value })}
                disabled={isSaving}
                placeholder="ej: 20-12345678-9 (CUIT), ABC123456XYZ (RFC)"
                className="form-input"
              />
              <small className="form-help">
                CUIT (AR), RFC (MX), EIN/SSN (US), BN/SIN (CA)
              </small>
            </div>
          </div>

          <div className="form-section">
            <h2 className="section-title">Seguridad y Acceso</h2>

            <div className="form-group">
              <label htmlFor="password">
                {isEditMode ? 'Nueva Contraseña (dejar vacío para no cambiar)' : 'Contraseña'}{' '}
                {!isEditMode && <span className="required">*</span>}
              </label>
              <input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required={!isEditMode}
                disabled={isSaving}
                placeholder="••••••••"
                className="form-input"
              />
              {!isEditMode && (
                <small className="form-help">
                  Mínimo 8 caracteres
                </small>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="tenantId">
                Cliente <span className="required">*</span>
              </label>
              <select
                id="tenantId"
                value={formData.tenantId}
                onChange={(e) => setFormData({ ...formData, tenantId: e.target.value })}
                required={!isEditMode}
                disabled={isSaving}
                className="form-select"
              >
                <option value="">Seleccionar cliente</option>
                {tenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.clientNumber
                      ? `${tenant.clientNumber.toString().padStart(4, '0')} - ${tenant.name}`
                      : tenant.name}
                  </option>
                ))}
              </select>
              {!isEditMode && tenants.length === 0 && (
                <small className="form-help" style={{ color: '#ffc107' }}>
                  No hay clientes disponibles. Debe crear un cliente primero.
                </small>
              )}
              {isEditMode && (
                <small className="form-help">
                  Solo SuperAdmin puede cambiar el cliente de un usuario
                </small>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="roleName">
                Rol <span className="required">*</span>
              </label>
              <select
                id="roleName"
                value={formData.roleName}
                onChange={(e) => setFormData({ ...formData, roleName: e.target.value as any })}
                required
                disabled={isSaving}
                className="form-select"
              >
                <option value="admin">Administrador (Propietario del cliente)</option>
                <option value="user_high">Usuario Alto - Acceso Completo</option>
                <option value="user_medium">Usuario Medio - Acceso Parcial</option>
                <option value="user_low">Usuario Bajo - Solo Lectura</option>
              </select>
            </div>

            {isEditMode && (
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    disabled={isSaving}
                    style={{ marginRight: '8px' }}
                  />
                  Usuario Activo
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Footer con botones de acción */}
        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/admin/dashboard?tab=users')}
            className="btn-secondary"
            disabled={isSaving}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={isSaving}
          >
            {isSaving ? 'Guardando...' : (isEditMode ? 'Guardar Cambios' : 'Crear Usuario')}
          </button>
        </div>
      </form>
    </div>
  );
}
